import React from 'react';
import { 
  Building2, 
  Users, 
  Award, 
  Globe, 
  MapPin, 
  Phone, 
  Mail,
  Calendar,
  Target,
  Lightbulb
} from 'lucide-react';

const About: React.FC = () => {
  const stats = [
    { icon: <Calendar className="h-8 w-8" />, value: "15+", label: "Years of Excellence", color: "text-blue-600" },
    { icon: <Users className="h-8 w-8" />, value: "500+", label: "Expert Engineers", color: "text-green-600" },
    { icon: <Globe className="h-8 w-8" />, value: "200+", label: "Global Clients", color: "text-purple-600" },
    { icon: <Award className="h-8 w-8" />, value: "50+", label: "Awards Won", color: "text-orange-600" }
  ];

  const values = [
    {
      icon: <Lightbulb className="h-12 w-12" />,
      title: "Innovation",
      description: "We constantly push the boundaries of technology to create cutting-edge solutions that solve real-world problems.",
      color: "bg-yellow-500"
    },
    {
      icon: <Target className="h-12 w-12" />,
      title: "Excellence",
      description: "We maintain the highest standards in everything we do, from code quality to customer service.",
      color: "bg-red-500"
    },
    {
      icon: <Users className="h-12 w-12" />,
      title: "Collaboration",
      description: "We believe in the power of teamwork and partnership to achieve extraordinary results.",
      color: "bg-blue-500"
    },
    {
      icon: <Globe className="h-12 w-12" />,
      title: "Global Impact",
      description: "We create solutions that make a positive difference in businesses and communities worldwide.",
      color: "bg-green-500"
    }
  ];

  return (
    <section id="about" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            About Brain Station 23
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            A leading software development company pioneering innovative solutions 
            for the digital age, now bringing world-class security expertise to AWS.
          </p>
        </div>

        {/* Company Story */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
          <div className="space-y-6">
            <h3 className="text-3xl font-bold text-gray-900">
              Pioneering Digital Excellence Since 2006
            </h3>
            <p className="text-lg text-gray-600 leading-relaxed">
              Brain Station 23 is Bangladesh's leading software development company, 
              founded with a vision to bridge the gap between innovative technology 
              and practical business solutions. For over 15 years, we have been at 
              the forefront of digital transformation.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed">
              Our expertise spans across web development, mobile applications, 
              enterprise software, cloud solutions, and now advanced AWS security 
              platforms. We combine deep technical knowledge with industry best 
              practices to deliver solutions that drive business growth.
            </p>
            <div className="flex items-center space-x-4">
              <Building2 className="h-8 w-8 text-primary-600" />
              <div>
                <div className="font-semibold text-gray-900">Established 2006</div>
                <div className="text-gray-600">15+ Years of Innovation</div>
              </div>
            </div>
          </div>
          
          {/* Company Stats */}
          <div className="grid grid-cols-2 gap-6">
            {stats.map((stat, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-6 text-center card-hover">
                <div className={`${stat.color} mb-4 flex justify-center`}>
                  {stat.icon}
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-2">{stat.value}</div>
                <div className="text-sm text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* AWS Security Guardian Story */}
        <div className="bg-gradient-to-r from-primary-50 to-secondary-50 rounded-2xl p-8 lg:p-12 mb-20">
          <div className="text-center mb-8">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">
              Why AWS Security Guardian?
            </h3>
            <p className="text-lg text-gray-600 max-w-4xl mx-auto">
              As cloud adoption accelerated, we recognized a critical gap in AWS security management. 
              Organizations needed more than just tools—they needed intelligent, proactive security 
              that could prevent incidents before they happen.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-primary-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lightbulb className="h-8 w-8 text-white" />
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-2">Innovation</h4>
              <p className="text-gray-600">
                Combining AI/ML with deep AWS expertise to create the next generation of security platforms.
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-secondary-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Target className="h-8 w-8 text-white" />
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-2">Expertise</h4>
              <p className="text-gray-600">
                15+ years of software development experience applied to solving complex security challenges.
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-orange-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="h-8 w-8 text-white" />
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-2">Excellence</h4>
              <p className="text-gray-600">
                Award-winning development practices ensuring enterprise-grade quality and reliability.
              </p>
            </div>
          </div>
        </div>

        {/* Company Values */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">Our Core Values</h3>
            <p className="text-lg text-gray-600">
              The principles that guide everything we do at Brain Station 23
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="text-center card-hover">
                <div className={`${value.color} text-white w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6`}>
                  {value.icon}
                </div>
                <h4 className="text-xl font-semibold text-gray-900 mb-3">{value.title}</h4>
                <p className="text-gray-600 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-gray-50 rounded-2xl p-8 lg:p-12">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                Get in Touch with Brain Station 23
              </h3>
              <p className="text-lg text-gray-600 mb-8">
                Ready to transform your AWS security? Our team of experts is here to help 
                you implement world-class security solutions tailored to your needs.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-center space-x-4">
                  <MapPin className="h-6 w-6 text-primary-600 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-gray-900">Headquarters</div>
                    <div className="text-gray-600">
                      8th Floor, Plot 2, Road 144<br />
                      Gulshan Avenue, Dhaka 1212<br />
                      Bangladesh
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-4">
                  <Phone className="h-6 w-6 text-primary-600 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-gray-900">Phone</div>
                    <div className="text-gray-600">+880 2 222 283 106</div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-4">
                  <Mail className="h-6 w-6 text-primary-600 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-gray-900">Email</div>
                    <div className="text-gray-600">
                      <div>info@brainstation-23.com</div>
                      <div>aws-security@brainstation-23.com</div>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-4">
                  <Globe className="h-6 w-6 text-primary-600 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-gray-900">Website</div>
                    <div className="text-gray-600">www.brainstation-23.com</div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-8 shadow-sm">
              <h4 className="text-xl font-semibold text-gray-900 mb-6">
                Global Presence
              </h4>
              <div className="space-y-6">
                <div>
                  <h5 className="font-semibold text-gray-900 mb-2">Bangladesh Office</h5>
                  <p className="text-gray-600 text-sm">
                    8th Floor, Plot 2, Road 144<br />
                    Gulshan Avenue, Dhaka 1212
                  </p>
                </div>
                
                <div>
                  <h5 className="font-semibold text-gray-900 mb-2">USA Office</h5>
                  <p className="text-gray-600 text-sm">
                    1201 N Orange Street<br />
                    Wilmington, DE 19801
                  </p>
                </div>
                
                <div>
                  <h5 className="font-semibold text-gray-900 mb-2">UK Office</h5>
                  <p className="text-gray-600 text-sm">
                    Kemp House, 160 City Road<br />
                    London, EC1V 2NX
                  </p>
                </div>
                
                <div className="pt-4 border-t">
                  <div className="text-sm text-gray-500">
                    <strong>Business Hours:</strong><br />
                    Sunday - Thursday: 9:00 AM - 6:00 PM (GMT+6)<br />
                    24/7 Support Available for Enterprise Clients
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
